/**
 * Nouveau modèle de services aligné sur la refactor backend (ServiceSubscription + ServiceSolution + ServiceStatus)
 * Anciennes propriétés (isActive, adblockSolutionType, noConsentSolutionType) supprimées.
 * Certaines enums legacy sont conservées à titre de compatibilité (dépréciées) mais ne doivent plus être utilisées dans les nouveaux écrans.
 */

// Enums backend
export enum ServiceType {
  SSP = 'SSP',
  ADBLOCK = 'ADBLOCK',
  NO_CONSENT = 'NO_CONSENT'
}

export enum ServiceSolution {
  ADBLOCK_LEGACY = 'ADBLOCK_LEGACY',
  ADBLOCK_WORKAROUND = 'ADBLOCK_WORKAROUND',
  NO_CONSENT_OUTBRAIN = 'NO_CONSENT_OUTBRAIN',
  NO_CONSENT_GAM = 'NO_CONSENT_GAM',
  SSP = 'SSP'
}

export enum ServiceStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  PAUSED = 'PAUSED',
  TERMINATED = 'TERMINATED'
}

export enum GAMType { DBLOCK = 'DBLOCK', PUBLISHER = 'PUBLISHER' }

// Champs config potentiels selon la solution (flatten côté front pour simplicité)
export interface ServiceConfigCommon {
  commissionPublisher?: number | null; // ADBLOCK_LEGACY
  commissionDblock?: number | null;    // ADBLOCK_LEGACY
  commissionPercentage?: number | null; // ADBLOCK_WORKAROUND, NO_CONSENT_*, SSP
  gamType?: GAMType | null;            // *_WORKAROUND, NO_CONSENT_GAM
  gamAccountId?: string | null;        // id GAM éventuel
  scriptVariant?: string | null;       // ADBLOCK_WORKAROUND
  integrationType?: string | null;     // SSP
  protocol?: string | null;            // SSP
  // Nouveaux identifiants sources reliés aux datasets externes
  outbrainSection?: string | null;     // NO_CONSENT_OUTBRAIN
  websiteName?: string | null;         // ADBLOCK_WORKAROUND (Adshield websiteName)
  siteKey?: string | null;             // SSP (trusted_stack site)
}

export interface ServiceSubscription extends ServiceConfigCommon {
  id: number;
  domainId: number;
  service: ServiceType;
  solution: ServiceSolution;
  status: ServiceStatus;
  startDate: string; // ISO
  endDate?: string | null;
  details?: string | null; // texte JSON sérialisé backend
  createdAt: string;
  updatedAt: string; // lastUpdated backend
  deletedAt?: string | null; // Date de soft delete pour déterminer l'état actif/inactif
  domain?: ServiceDomain;
  config?: any | null; // objet config brut renvoyé (optionnel)
}

export interface ServiceDomain {
  id: number;
  name: string;
  url: string;
  networkId?: number;
  network?: { id: number; name: string; bqKey: string };
}

// Payload création (aligné createServiceSubscription backend)
export interface CreateServiceSubscriptionRequest extends Partial<ServiceConfigCommon> {
  domainId: number;
  service: ServiceType;
  solution: ServiceSolution;
  status?: ServiceStatus;
  startDate: string; // ISO (YYYY-MM-DD)
  endDate?: string | null;
  details?: string | null; // string JSON
}

// Payload mise à jour (pas de changement de service/solution)
export interface UpdateServiceSubscriptionRequest extends Partial<ServiceConfigCommon> {
  status?: ServiceStatus;
  endDate?: string | null;
  details?: string | null;
}

// Données de formulaire UI
export interface ServiceSubscriptionFormData extends ServiceConfigCommon {
  service: ServiceType | '';
  solution: ServiceSolution | '';
  status: ServiceStatus;
  startDate: string;
  endDate: string; // vide = null
  details: string; // JSON brut / textarea
}

export const INITIAL_FORM_DATA: ServiceSubscriptionFormData = {
  service: '',
  solution: '',
  status: ServiceStatus.ACTIVE,
  commissionPublisher: null,
  commissionDblock: null,
  commissionPercentage: null,
  gamType: null,
  gamAccountId: null,
  scriptVariant: null,
  integrationType: null,
  protocol: null,
  outbrainSection: null,
  websiteName: null,
  siteKey: null,
  startDate: new Date().toISOString().split('T')[0],
  endDate: '',
  details: ''
};

// Compat mapping service -> solutions possibles
export const SERVICE_TO_SOLUTIONS: Record<ServiceType, ServiceSolution[]> = {
  [ServiceType.ADBLOCK]: [ServiceSolution.ADBLOCK_LEGACY, ServiceSolution.ADBLOCK_WORKAROUND],
  [ServiceType.NO_CONSENT]: [ServiceSolution.NO_CONSENT_OUTBRAIN, ServiceSolution.NO_CONSENT_GAM],
  [ServiceType.SSP]: [ServiceSolution.SSP]
};

export const SERVICE_OPTIONS = [
  { value: ServiceType.SSP, label: 'SSP' },
  { value: ServiceType.ADBLOCK, label: 'Adblock' },
  { value: ServiceType.NO_CONSENT, label: 'No Consent' }
];

// Utilitaire statut actif
export const isActiveStatus = (status: ServiceStatus) => status === ServiceStatus.ACTIVE;

// ---- Statistiques (optionnel; simplifié) ----
export interface ServiceStatisticsSummary {
  total: number;
  byStatus: Record<ServiceStatus, number>;
  byService: Record<ServiceType, number>;
  bySolution: Record<ServiceSolution, number>;
  averageCommission?: number;
}

export type SortDirection = 'asc' | 'desc';
export type ServiceSubscriptionSortField = 'service' | 'solution' | 'status' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt';

export interface ServiceSubscriptionSortOptions {
  field: ServiceSubscriptionSortField;
  direction: SortDirection;
}

// Réponse paginée (placeholder si pagination ajoutée)
export interface ServiceSubscriptionPaginatedResponse {
  subscriptions: ServiceSubscription[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Export data placeholder
export interface ServiceExportData {
  subscriptions: ServiceSubscription[];
  exportDate: string;
}
